'use client'
import { reviewApi, ReviewApiError, ReviewMe } from "@/apis/review"
import { useUserStore } from "@/store/useUserStore"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { useLocale } from "next-intl"
import { useEffect, useState } from "react"

export const ME_KEY = ['review', 'me']

/** 当前登录用户在审核平台的状态；未登录时不请求 */
export function useReviewMe() {
  const isLogined = useUserStore((s: any) => s.isLogined)
  const isPending = useUserStore((s: any) => s.isPending)
  const query = useQuery<ReviewMe>({
    queryKey: ME_KEY,
    queryFn: reviewApi.me,
    enabled: !!isLogined,
    staleTime: 30_000,
  })
  return { ...query, isLogined: !!isLogined, authPending: !!isPending }
}

export function useRefreshMe() {
  const client = useQueryClient()
  return () => client.invalidateQueries({ queryKey: ME_KEY })
}

export function useReviewRules() {
  return useQuery({ queryKey: ['review', 'rules'], queryFn: reviewApi.rules, staleTime: 10 * 60_000 })
}

/** 每秒刷新的当前时间（倒计时用） */
export function useNow(active = true) {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (!active) return
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [active])
  return now
}

export function formatClock(seconds: number) {
  const s = Math.max(0, Math.floor(seconds))
  const m = Math.floor(s / 60)
  return `${m}:${String(s % 60).padStart(2, '0')}`
}

export function useFormatTime() {
  const locale = useLocale()
  /** 接口时间统一是 Unix 秒 */
  return (seconds: number) => {
    if (!seconds) return ''
    const date = new Date(seconds * 1000)
    return new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }).format(date)
  }
}

export function errorStatus(e: unknown): number {
  return e instanceof ReviewApiError ? e.status : 0
}

const AUTO_TRANSLATE_KEY = 'review:auto-translate'

/** 自动翻译开关（本地记住）：打开后每张任务 / 每道考题载入即翻成界面语言 */
export function useAutoTranslate() {
  const [on, setOn] = useState(false)
  useEffect(() => {
    try { setOn(window.localStorage.getItem(AUTO_TRANSLATE_KEY) === '1') } catch { /* 隐私模式 */ }
  }, [])
  const set = (value: boolean) => {
    setOn(value)
    try { window.localStorage.setItem(AUTO_TRANSLATE_KEY, value ? '1' : '0') } catch { /* 隐私模式 */ }
  }
  return [on, set] as const
}

/** 详情页（角色详情、审核单详情、考试回看）的翻译开关：跟随「自动翻译」偏好，手动点过就按手动 */
export function useDetailTranslate() {
  const [auto, setAuto] = useAutoTranslate()
  const [manual, setManual] = useState<boolean | null>(null)
  const showing = manual ?? auto
  return {
    showing,
    auto,
    setAuto: (value: boolean) => { setAuto(value); setManual(null) },
    toggle: () => setManual(!showing),
  }
}

export type MachineState = 'flag' | 'ok' | 'maybe' | 'none'

/** 机审值（-1 未检 / 0 安全 / 1 违禁 / 2 可疑·性感）→ 提示四态 */
export function machineState(value: number): MachineState {
  return value === 1 ? 'flag' : value === 0 ? 'ok' : value === 2 ? 'maybe' : 'none'
}

export const MACHINE_DOT: Record<MachineState, string> = {
  flag: 'bg-theme-error', ok: 'bg-theme-green', maybe: 'bg-[#F5A524]', none: 'bg-theme-primary/20',
}
